import { Coupon, CouponAssessment, UsedCouponsResult } from '../../../../../../types/generated.types';
import { CouponWithState } from '../../../../../../context/CouponsManageContext';

/**
 * Creates mock coupons for testing/demo purposes
 */
export const createMockCoupons = (quantity: number, companyId: string | unknown): Coupon[] => {
  return Array.from({ length: quantity }, (_, i) => ({
    _id: `mock-${i + 1}`,
    companyId,
    discount: 'Rabatt',
    smallTitle: 'Liten titel',
    description: 'Beskrivning beskrivning beskrivning',
    terms: 'Villkor villkor villkor villkor villkor villkor',
    codeType: 'KODTYP',
    code: '0000000000000',
    estimatedSavings: 0,
    usedCouponsResult: {
      totalAmount: 0,
      booklets: [],
    } as UsedCouponsResult,
    assessment: {
      rating: 4.5,
      motivation: 'Motivering sammanfattad i en rubrik med säljande ton för annonsörerna',
      expectedEffect: 'AI-genererad bedömning, ofta låg - hög, smal - bred + kommentar',
      profitability: 'AI-genererad bedömning, ofta låg - hög, smal - bred + kommentar',
      targetGroupAdaptation: 'AI-genererad bedömning, ofta låg - hög, smal - bred + kommentar',
      shouldShowAssessment: true,
    } as CouponAssessment,
  }));
};

/**
 * Creates a single empty coupon template for new coupon creation
 */
export const createEmptyCoupon = (companyId: string | unknown): CouponWithState => {
  return {
    id: `new-coupon-${Date.now()}`,
    isEditing: false,
    isExisting: false,
    data: {
      companyId,
      discount: '',
      smallTitle: '',
      description: '',
      terms: 'Kan ej kombineras med andra erbjudanden.',
      codeType: 'TEXT',
      code: '',
      estimatedSavings: null,
      assessment: {
        rating: null,
        motivation: '',
        expectedEffect: '',
        profitability: '',
        targetGroupAdaptation: '',
        shouldShowAssessment: true,
      } as Partial<CouponAssessment>,
    },
  };
};

/**
 * Creates empty coupon templates for new coupon creation
 */
export const createEmptyCoupons = (quantity: number, companyId: string | unknown): CouponWithState[] => {
  return Array.from({ length: quantity }, (_, i) => ({
    id: `new-coupon-${i}`,
    isEditing: true,
    isExisting: false,
    data: {
      companyId,
      discount: '',
      smallTitle: '',
      description: '',
      terms: 'Kan ej kombineras med andra erbjudanden.',
      codeType: 'TEXT',
      code: '',
      estimatedSavings: null,
      assessment: {
        rating: null,
        motivation: '',
        expectedEffect: '',
        profitability: '',
        targetGroupAdaptation: '',
        shouldShowAssessment: true,
      } as Partial<CouponAssessment>,
    },
  }));
};

/**
 * Converts an array of Coupon objects to CouponWithState format
 */
export const convertCouponsToState = (
  coupons: Coupon[],
  companyId: string | unknown,
  startInEditingMode: boolean = false
): CouponWithState[] => {
  return coupons.map((coupon, index) => ({
    id: String(coupon._id) || `coupon-${index}`,
    isEditing: startInEditingMode,
    isExisting: true, // These coupons exist in the database
    data: {
      ...coupon,
      companyId,
    },
  }));
};

/**
 * Converts mock coupons to CouponWithState format
 */
export const convertMockCouponsToState = (
  quantity: number,
  companyId: string | unknown
): CouponWithState[] => {
  const mockCoupons = createMockCoupons(quantity, companyId);
  return mockCoupons.map((coupon, index) => ({
    id: String(coupon._id) || `mock-coupon-${index}`,
    isEditing: false,
    isExisting: false, // Mock coupons are not in database
    data: {
      ...coupon,
      companyId,
    },
  }));
};

/**
 * Sorts coupons by position.
 * Coupons with position are sorted by position value (ascending).
 * Coupons without position (null/undefined) are placed at the end.
 * 
 * @param coupons - Array of coupons to sort
 * @returns New sorted array (does not mutate original)
 */
export const sortCouponsByPosition = (coupons: CouponWithState[]): CouponWithState[] => {
  return [...coupons].sort((a, b) => {
    const aPosition = a.data.position ?? null;
    const bPosition = b.data.position ?? null;

    // Both have positions: sort by position value
    if (aPosition !== null && bPosition !== null) {
      return aPosition - bPosition;
    }

    // Only a has position: a comes first
    if (aPosition !== null && bPosition === null) {
      return -1;
    }

    // Only b has position: b comes first
    if (aPosition === null && bPosition !== null) {
      return 1;
    }

    // Both are null: maintain original order
    return 0;
  });
};

